import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Button,
  Typography,
  FormHelperText
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useTranslation } from "react-i18next";
import { Add, Clear } from "@mui/icons-material";
import { v4 as uuid } from "uuid";
import { enterKeyCode, resourceObjectTypes } from "../../../constants";
import { useFormikContext } from "formik";

const {
  fillInTheBlankTextType,
  fillInTheBlankAnswerType
} = resourceObjectTypes;

const useStyles = makeStyles(theme => ({
  fillBlankTextField: {
    marginRight: theme.spacing(1)
  },
  clearButton: {
    backgroundColor: theme.palette.primary.main,
    position: "absolute",
    padding: "7px",
    right: "-3px",
    top: "-3px",
    height: 0,
    width: 0,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark
    }
  },
  clearButtonIcon: {
    color: theme.palette.common.white,
    fontSize: "0.75rem"
  },
  textFieldInput: {
    textOverflow: "ellipsis"
  }
}));

const newFillInTheBlank = (currentFillInTheBlanks, type) => ({
  type,
  position: currentFillInTheBlanks.length,
  id: uuid(),
  text: "",
  /* if the newly added blank is an answer blank, set the new answer
  num to the current number of answers plus one */
  ...(type === fillInTheBlankAnswerType && {
    answerNum:
      currentFillInTheBlanks.filter(
        ({ type }) => type === fillInTheBlankAnswerType
      ).length + 1
  })
});

/**
 * Returns the label for the fill in the blank texts Text Fields.
 */
const getTextLabel = (t, fillInTheBlanks, textPosition) => {
  const fillInTheBlankTexts = fillInTheBlanks.filter(
    ({ type }) => type === fillInTheBlankTextType
  );
  const textNum = fillInTheBlankTexts.findIndex(
    ({ position }) => position === textPosition
  );
  return t("editQuestion.textNumLabel", {
    textNum: textNum + 1
  });
};

const getAnswerLabel = (t, fillInTheBlanks, answerPosition) => {
  const fillInTheBlankAnswers = fillInTheBlanks.filter(
    ({ type }) => type === fillInTheBlankAnswerType
  );
  const answerNum = fillInTheBlankAnswers.findIndex(
    ({ answerNum }) => answerNum === answerPosition
  );
  return t("editQuestion.answerNumLabel", {
    answerNum: answerNum + 1
  });
};

const FillInTheBlankTextField = ({
  text,
  label,
  onSave,
  onDelete,
  isFocused,
  disabled
}) => {
  const classes = useStyles();
  const [value, setValue] = useState(text);
  const [inputWidth, setInputWidth] = useState(180);
  const inputRef = useRef(null);

  useEffect(() => {
    // only necessary for MVP to focus on the last blank if the
    // user tries to add a second blank in a row
    if (isFocused) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  const handleSaveText = value => {
    onSave(value);
    setInputWidth(inputRef.current.scrollWidth + 8);
  };

  return (
    <Box
      mr={1}
      mt={2.5}
      width={inputWidth}
      minWidth={180}
      maxWidth={inputWidth}
    >
      <TextField
        className={classes.fillBlankTextField}
        label={label}
        fullWidth
        autoFocus={true}
        value={value}
        variant="filled"
        disabled={disabled}
        InputProps={{
          onKeyDown: event =>
            event.keyCode === enterKeyCode && handleSaveText(value), // saves when enter is pressed
          onBlur: () => handleSaveText(value),
          endAdornment: (
            <IconButton
              onClick={onDelete}
              classes={{
                root: classes.clearButton
              }}
              size="large"
            >
              <Clear className={classes.clearButtonIcon} />
            </IconButton>
          ),
          inputProps: {
            ref: inputRef,
            className: classes.textFieldInput
          }
        }}
        onChange={e => setValue(e.target.value)}
      />
    </Box>
  );
};

const FillInTheBlankFields = ({ isSurveyQuestion }) => {
  const { t } = useTranslation();
  const { errors, submitCount, values, setFieldValue } = useFormikContext();
  // keeping track of the editable index is only necessary for mvp to focus on the last blank
  // if the user tries to add a second blank
  const [currentEditableIndex, setCurrentEditableIndex] = useState(null);
  const [addBlankError, setAddBlankError] = useState(false);

  const handleAddText = () => {
    setAddBlankError(false);
    const newFillInTheBlanks = [...values.fillInTheBlanks];
    newFillInTheBlanks.push(
      newFillInTheBlank(values.fillInTheBlanks, fillInTheBlankTextType)
    );
    setCurrentEditableIndex(newFillInTheBlanks.length - 1);
    setFieldValue("fillInTheBlanks", newFillInTheBlanks);
  };

  const handleAddBlank = () => {
    const newFillInTheBlanks = [...values.fillInTheBlanks];
    let currentFillInTheBlank =
      newFillInTheBlanks[values.fillInTheBlanks.length - 1];
    // Since we're not allowing multiple answers in a row for MVP, we will only
    // add the blank if there is no blank before it
    if (
      !currentFillInTheBlank ||
      currentFillInTheBlank.type === fillInTheBlankTextType
    ) {
      setAddBlankError(false);
      newFillInTheBlanks.push(
        newFillInTheBlank(values.fillInTheBlanks, fillInTheBlankAnswerType)
      );
    } else {
      setAddBlankError(true);
    }
    setCurrentEditableIndex(newFillInTheBlanks.length - 1);
    setFieldValue("fillInTheBlanks", newFillInTheBlanks);
  };

  const handleSaveFillInTheBlank = (newText, arrayIndex) => {
    setAddBlankError(false);
    const newFillInTheBlanks = [...values.fillInTheBlanks];
    newFillInTheBlanks[arrayIndex] = {
      ...newFillInTheBlanks[arrayIndex],
      text: newText
    };
    setFieldValue("fillInTheBlanks", newFillInTheBlanks);
    setCurrentEditableIndex(null);
  };

  /**
   * Called when the user clicks on the delete button in a text field to delete the text or a blank
   * @param {number} fillInTheBlankIndex The index where the fillInTheBlank object should be removed
   */
  const handleDelete = fillInTheBlankIndex => {
    let newFillInTheBlanks = [...values.fillInTheBlanks];
    const deletedFillInTheBlank = newFillInTheBlanks.splice(
      fillInTheBlankIndex,
      1
    )[0];
    newFillInTheBlanks = newFillInTheBlanks.map((fillInTheBlank, index) => ({
      ...fillInTheBlank,
      position: index,
      /* If the deleted blank is an answer blank, we need to reduce the answer num of all
      blanks that follow it by 1 */
      ...(fillInTheBlank.type === fillInTheBlankAnswerType && {
        answerNum: (fillInTheBlank.answerNum -=
          fillInTheBlank.answerNum > deletedFillInTheBlank.answerNum ? 1 : 0)
      })
    }));
    setFieldValue("fillInTheBlanks", newFillInTheBlanks);
  };

  return (
    <Box
      display="flex"
      alignItems="flex-start"
      mt={1.5}
      flexDirection={{ xs: "column", md: "row" }}
    >
      <Box mr={11.75} display="flex" alignItems="center" mt={4.5}>
        <Typography variant="h5">
          {t("editQuestion.fillInTheBlankQuestionTitle")}
        </Typography>
      </Box>
      <Box display="flex" flex="1" flexWrap="wrap" overflow="hidden">
        {values.fillInTheBlanks &&
          values.fillInTheBlanks.map((currentFillInTheBlank, index) =>
            currentFillInTheBlank.type === fillInTheBlankTextType ? (
              <FillInTheBlankTextField
                key={
                  currentFillInTheBlank.type + "-" + currentFillInTheBlank.id
                }
                onDelete={() => handleDelete(index)}
                onSave={newText => handleSaveFillInTheBlank(newText, index)}
                text={currentFillInTheBlank.text}
                isFocused={currentEditableIndex === index}
                label={getTextLabel(
                  t,
                  values.fillInTheBlanks,
                  currentFillInTheBlank.position
                )}
              />
            ) : (
              <FillInTheBlankTextField
                key={
                  currentFillInTheBlank.type + "-" + currentFillInTheBlank.id
                }
                onDelete={() => handleDelete(index)}
                onSave={newText => handleSaveFillInTheBlank(newText, index)}
                text={currentFillInTheBlank.text}
                isFocused={currentEditableIndex === index}
                label={getAnswerLabel(
                  t,
                  values.fillInTheBlanks,
                  currentFillInTheBlank.answerNum
                )}
                disabled={isSurveyQuestion}
              />
            )
          )}
        <Box display="flex" alignItems="center" mt={2.5} minHeight={56}>
          <Button
            color="primary"
            onClick={() => handleAddText()}
            startIcon={<Add />}
          >
            {t("editQuestion.fillInTheBlankAddTextBtn")}
          </Button>
          <Button
            color="primary"
            onClick={() => handleAddBlank()}
            startIcon={<Add />}
          >
            {t("editQuestion.fillInTheBlankAddBlankBtn")}
          </Button>
        </Box>
        {!!errors.fillInTheBlanks && !addBlankError && submitCount > 0 && (
          <Box mt={2} width="100%">
            <FormHelperText error={true}>
              {errors.fillInTheBlanks}
            </FormHelperText>
          </Box>
        )}
        {!!addBlankError && (
          <Box mt={2} width="100%">
            <FormHelperText error={true}>
              {t("editQuestion.blanksBetweenTexts")}
            </FormHelperText>
          </Box>
        )}
      </Box>
    </Box>
  );
};

FillInTheBlankFields.propTypes = {};

export default FillInTheBlankFields;
